import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import NoticeBar from 'components/NoticeBar'
import Calendar from '@groovehq/internal-design-system/lib/assets/icons/Calendar'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import { doSetFlag } from 'ducks/flags/operations'
import { selectCurrentUserIsAdminOrOwner } from 'ducks/currentUser/selectors'
import { doFetchBillingData } from 'ducks/billing/operations'
import { selectAnnualOffer } from 'ducks/billing/selectors/selectAnnualOffer'
import { useAnnualOfferFigures } from 'ducks/billing/useAnnualOfferFigures'
import { useOpenAnnualSwitchDrawer } from 'ducks/billing/useOpenAnnualSwitchDrawer'
import { selectNextBillDate } from 'ducks/billing/selectors/selectNextBillDate'
import { selectShowAnnualBillingReminder } from 'ducks/billing/selectors/selectShowAnnualBillingReminder'
import { getFormattedPrice } from 'util/numbers'
import { styles } from './styles'

const epochSeconds = () => Math.floor(Date.now() / 1000)

export default function AnnualBillingReminder() {
  const dispatch = useDispatch()
  const figures = useAnnualOfferFigures()
  const canLoadRenewal = useSelector(
    state =>
      selectAnnualOffer(state)?.surfaces?.reminder === true &&
      selectCurrentUserIsAdminOrOwner(state)
  )
  useEffect(
    () => {
      if (canLoadRenewal) dispatch(doFetchBillingData())
    },
    [dispatch, canLoadRenewal]
  )
  const [now, setNow] = useState(epochSeconds)
  useEffect(() => {
    const timer = setInterval(() => setNow(epochSeconds()), 1000)
    return () => clearInterval(timer)
  }, [])
  const show = useSelector(state =>
    selectShowAnnualBillingReminder(state, now, figures.yearlySaving)
  )
  const nextBillDate = useSelector(selectNextBillDate)
  const openAnnual = useOpenAnnualSwitchDrawer({
    timing: 'renewal',
    source: 'reminder',
  })
  const snooze = useCallback(
    () => dispatch(doSetFlag(`annual_reminder_snoozed_${nextBillDate}`)),
    [dispatch, nextBillDate]
  )
  const dismiss = useCallback(
    () => dispatch(doSetFlag('annual_reminder_dismissed')),
    [dispatch]
  )
  if (
    !show ||
    !figures.ready ||
    !Number.isInteger(nextBillDate) ||
    nextBillDate <= 0 ||
    !Number.isFinite(figures.monthlyTotal) ||
    figures.monthlyTotal <= 0
  )
    return null

  const money = value =>
    getFormattedPrice(Math.round(value), figures.currency).replace(/\.00$/, '')
  return (
    <NoticeBar css={styles.annualReminderBar}>
      <section
        className="grui"
        aria-label="Annual billing reminder"
        css={styles.annualReminderContent}
      >
        <span
          className="grui"
          aria-hidden="true"
          css={styles.annualReminderIcon}
        >
          <Calendar focusable="false" />
        </span>
        <div className="grui" css={styles.annualReminderText}>
          <strong className="grui">
            {`Your monthly invoice of ${money(
              figures.monthlyTotal
            )} is due ${moment.unix(nextBillDate).format('dddd, D MMMM')}.`}
          </strong>{' '}
          Switch to annual billing before then and keep{' '}
          <strong className="grui">{money(figures.yearlySaving)}</strong>
          {` a year on the same ${
            figures.isSeatBased ? `${figures.seats} seats` : 'plan'
          }.`}
        </div>
        <Button
          type="info"
          css={[styles.annualReminderButton, styles.annualReminderPrimary]}
          onClick={openAnnual}
        >
          See annual price
        </Button>
        <Button
          type="tertiary"
          css={styles.annualReminderButton}
          onClick={snooze}
        >
          Remind me next month
        </Button>
        <button
          className="grui"
          type="button"
          aria-label="Dismiss reminder"
          css={[styles.annualReminderButton, styles.annualReminderDismiss]}
          onClick={dismiss}
        >
          ×
        </button>
      </section>
    </NoticeBar>
  )
}
